import * as ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';

export {
  applyExcelTableBorders,
  collectAnoNeStatusColumnsFromAoA,
  collectBudovaPripojeniCheckStatusColumns,
  createBasicAoAStyleSheet,
  estimateExcelColumnWidths,
  findAoAHeaderColumnIndex,
  hexToExcelArgb,
  lightenHexForExcelFill,
  resolveAnoNeExcelStyle,
  resolveDiaryStatusExcelStyle,
  resolveObjectStatusExcelFillArgb,
  resolvePolygonPlanStateExcelStyle,
  styleAoAExportSheet,
  styleExcelHeaderRow,
  BOOL_ANO_NE_EXCEL_FILLS,
  EXCEL_EXPORT_HEADER_FILL_ARGB,
  POLYGON_PLAN_STATE_EXCEL_FILLS,
} from './xlsx-export-style.util';
export type { AoAExportStyleOptions, AoAExportStatusColumnStyle } from './xlsx-export-style.util';

/** Excel list max. 31 znaků; zakázané znaky nahradíme mezerou. */
function safeSheetName(name: string): string {
  return name.replaceAll(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31) || 'List';
}

/**
 * Stáhne .xlsx s jedním nebo více listy (AOA = první řádek = hlavička).
 * Používá stejný stack jako existující exporty (`xlsx`).
 */
export function downloadXlsxWorkbook(
  filename: string,
  sheets: {
    name: string;
    aoa: (string | number | boolean | null | undefined)[][];
    /** Volitelné šířky sloupců (wch) pro lepší čitelnost exportu. */
    cols?: Array<{ wch?: number }>;
    /** Volitelná post-process úprava worksheetu (např. stylování). */
    decorateSheet?: (worksheet: XLSX.WorkSheet) => void;
  }[],
): void {
  const wb = XLSX.utils.book_new();
  for (const s of sheets) {
    const rows = s.aoa.map((r) => r.map((c) => (c ?? ''))) as (string | number)[][];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    if (Array.isArray(s.cols) && s.cols.length > 0) {
      (ws as unknown as { ['!cols']?: Array<{ wch?: number }> })['!cols'] = s.cols;
    }
    if (typeof s.decorateSheet === 'function') {
      s.decorateSheet(ws);
    }
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName(s.name));
  }
  XLSX.writeFile(wb, filename);
}

/**
 * Stáhne .xlsx přes ExcelJS — podporuje fonty, numFmt a šířky sloupců (pro formátované exporty).
 */
export async function downloadExcelJsAoAWorkbook(
  filename: string,
  sheets: {
    name: string;
    aoa: (string | number | boolean | null | undefined)[][];
    cols?: Array<{ width?: number; wch?: number }>;
    styleSheet?: (worksheet: ExcelJS.Worksheet, rowCount: number) => void;
  }[],
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  for (const s of sheets) {
    const ws = workbook.addWorksheet(safeSheetName(s.name));
    const rows = s.aoa.map((r) => r.map((c) => (c === '' || c == null ? '' : c)));
    ws.addRows(rows);
    if (Array.isArray(s.cols) && s.cols.length > 0) {
      ws.columns = s.cols.map((c) => ({ width: c.width ?? c.wch ?? 10 }));
    }
    if (typeof s.styleSheet === 'function') {
      s.styleSheet(ws, rows.length);
    }
  }
  const out = await workbook.xlsx.writeBuffer();
  const blob = new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export function cellStr(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'Ano' : 'Ne';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
